import React from 'react';
import ReactDOM from 'react-dom/client';
import { AuthenticationResult, EventMessage, EventType, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import './index.css';
import { msalConfig } from '@/features/auth/authConfig.ts';
import { getEnvironmentVariable } from '@/shared/utils/shared.environment.utils.ts';
import { setupApiAuth } from '@/shared/api/apiClient';
import App from './App';

/**
 * Initialize MSAL authentication instance
 */
async function setupMsalAuth() {
  const msalInstance = new PublicClientApplication(msalConfig);
  await msalInstance.initialize();

  // Handle redirect promise (captures auth result from Azure AD redirects)
  await msalInstance.handleRedirectPromise();

  // Set active account if available
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  // Add event callback for login success
  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const {account} = event.payload as AuthenticationResult;
      msalInstance.setActiveAccount(account);
    }
  });

  return msalInstance;
}

// Initialize MSAL and configure API authentication
const msalInstance = await setupMsalAuth();
setupApiAuth(msalInstance);

// Find and validate root element
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

// Get base URL for SignalR
const hubUrl: string = getEnvironmentVariable('VITE_API_ENDPOINT');

// Render the application
ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <MsalProvider instance={msalInstance}>
      <App hubUrl={hubUrl}/>
    </MsalProvider>
  </React.StrictMode>
);